import { useMemo, useState } from 'react';

import { DisplayComponent } from '@/components/DisplayComponent';
import { Header } from '@/components/Header';
import { Slogan } from '@/components/Slogan';
import {
    ELoanUsage,
    EVehicleAssetType,
    setLoanUsage,
    setVehicleAssetType,
} from '@/context/borrower';
import {
    EDisplays,
    selectLogo,
    selectPartner,
    setDisplay,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { IMultiAssetTile } from '@/partners/partnerTypes';
import { getPrimaryColor } from '@/utils/getPrimaryColor';
import { hexToRgb } from '@/utils/hexToRgb';

export const testId = 'MultiAssetJourney';

const DEFAULT_ASSET_TILES: IMultiAssetTile[] = [
    {
        label: 'Car',
        image: '',
        meAsset: EVehicleAssetType.PASSENGER_VEHICLE,
        financeAsset: EVehicleAssetType.PASSENGER_VEHICLE,
    },
    {
        label: 'Caravan',
        image: '',
        meAsset: EVehicleAssetType.CARAVAN,
        financeAsset: EVehicleAssetType.CARAVAN,
    },
    {
        label: 'Bike',
        image: '',
        meAsset: EVehicleAssetType.MOTORCYCLE,
        financeAsset: EVehicleAssetType.MOTORCYCLE,
    },
    {
        label: 'Truck',
        image: '',
        meAsset: EVehicleAssetType.TRUCK_GT_4_5,
        financeAsset: EVehicleAssetType.TRUCK_GT_4_5,
    },
    {
        label: 'Personal',
        image: '',
        meAsset: EVehicleAssetType.PERSONAL_LOAN,
        financeAsset: EVehicleAssetType.PERSONAL_LOAN,
    },
    {
        label: 'Business',
        image: '',
        meAsset: EVehicleAssetType.BUSINESS_LOAN,
        financeAsset: EVehicleAssetType.BUSINESS_LOAN,
    },
];

function getLoanUsageForAsset(assetType: EVehicleAssetType): ELoanUsage {
    if (
        assetType === EVehicleAssetType.BUSINESS_LOAN ||
        assetType === EVehicleAssetType.BUSINESS_LOAN_LOC
    ) {
        return ELoanUsage.BUSINESS;
    }
    return ELoanUsage.PERSONAL;
}

export function MultiAssetJourney(): JSX.Element {
    const baseId = 'screen-multiAssetJourney';
    const dispatch = useAppDispatch();
    const partner = useAppSelector(selectPartner);
    const logo = useAppSelector(selectLogo);
    const [hoveredTile, setHoveredTile] = useState<string | null>(null);
    const primaryColor = getPrimaryColor()?.trim() || '#FF5D7A4D';
    const tileBackground = hexToRgb(primaryColor, 0.3);
    const tileBackgroundHover = hexToRgb(primaryColor, 0.6);

    const assetTiles = useMemo(() => {
        const tiles = partner.multiAssetJourney?.assetTiles;
        if (tiles && tiles.length > 0) {
            return tiles;
        }
        return DEFAULT_ASSET_TILES;
    }, [partner.multiAssetJourney?.assetTiles]);

    function handleSelect(tile: IMultiAssetTile): void {
        dispatch(setVehicleAssetType(tile.financeAsset));
        dispatch(setLoanUsage(getLoanUsageForAsset(tile.financeAsset)));
        dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
    }

    return (
        <DisplayComponent baseId={baseId} data-testid={testId} fillHeight>
            <Header />
            <Slogan />
            <div className='flex flex-col -mx-4 px-4 h-full flex-1'>
                <h3
                    className='text-left flex-shrink-0'
                    style={{
                        fontWeight: 700,
                        fontSize: '11px',
                        lineHeight: '100%',
                        marginTop: '20px',
                        marginBottom: '16px',
                    }}
                >
                    What Are You Looking To Finance?
                </h3>
                <div
                    className={`grid grid-cols-2 w-full flex-1 ${
                        assetTiles.length >= 8
                            ? 'gap-x-4 gap-y-3'
                            : 'gap-x-5 gap-y-4'
                    }`}
                    style={{
                        gridTemplateRows: `repeat(${Math.ceil(
                            assetTiles.length / 2
                        )}, 1fr)`,
                        paddingBottom: '16px',
                    }}
                >
                    {assetTiles.map((tile) => {
                        const tileKey = tile._id ?? tile.label;
                        const isHovered = hoveredTile === tileKey;
                        const isLargeLayout = assetTiles.length <= 6;
                        return (
                            <button
                                key={tileKey}
                                type='button'
                                onMouseEnter={(): void =>
                                    setHoveredTile(tileKey)
                                }
                                onMouseLeave={(): void => setHoveredTile(null)}
                                onClick={(): void => handleSelect(tile)}
                                className={`rounded-2xl flex flex-col items-center justify-center transition-colors shadow-sm h-full min-h-0 ${
                                    isLargeLayout ? 'p-4' : 'p-3'
                                }`}
                                style={{
                                    backgroundColor: isHovered
                                        ? tileBackgroundHover
                                        : tileBackground,
                                }}
                            >
                                <img
                                    src={tile.image || logo}
                                    alt={tile.label}
                                    className={`w-auto object-contain flex-shrink ${
                                        isLargeLayout
                                            ? 'max-h-[75%] mb-3'
                                            : 'max-h-[55%] mb-2'
                                    }`}
                                />
                                <span
                                    className={`font-bold text-black flex-shrink-0 ${
                                        isLargeLayout ? 'text-base' : 'text-sm'
                                    }`}
                                >
                                    {tile.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </DisplayComponent>
    );
}
